<template>
  <main class="flex flex-col items-center">
    <Loader
      :loader="isTopicLoading"
      :label="$t('labels.generic', { pt: 'Carregando...', en: 'Loading...' })"
      loaderType="progress"
    />
    <BreadcrumbForum v-if="isTeacherOrAdmin() && !isTopicLoading"/>
    <section
      class="!max-w-[1344px] custom-container-forum mt-6"
      :class="[{'hidden' : isTopicLoading}]" 
    > 
    <div class="max-w-[1113px] mx-auto">
      <button
      class="mb-6 h-8 py-0.5 flex gap-3 items-center btn-simple px-0"
      @click="backToForumView()"
      >
      <v-icon name="co-arrow-left" width="24" height="24" />
      <span>
        {{
          $t("labels.generic", {
            pt: "Voltar para fórum",
            en: "Go back to forum",
          })
        }}
        </span>
      </button>
    </div>
      <TopicCardDetailed :topic="topic" @denounceTopic="toggleDenounceDialog(true)"
      />
      <div class="flex justify-center w-full">
        <ListComments
          :comments="comments"
          :topic="topic"
          :totalComments="totalComments"
          :query="query"
          :numberOfCommentsPerPage="numberOfCommentsPerPage"
          @deleteComment="refreshComment"
          @paginate="changePage"
          @replyComment="replyComment"
        >
        </ListComments>
      </div>
      <div v-if="topic?.status === status.ACTIVE" id="box-comment" class="flex justify-center w-full">
        <CommentInput
          :topicId="topicId"
          :replyOfComment="replyOfComment"
          @isSubmitting="isSubmittingComment"
          @removeReplyComment="removeReplyComment"
          @commentCreated="commentCreated"
        />
      </div>
      <Dialog
        group="denounce"
        v-model:visible="isDenounceDialogVisible"
        :style="{ maxWidth: '540px' }"
        class="w-11/12 custom-dialogForum text-h-neutral-900"
        modal
      >
        <template #header>
          <div class="flex justify-center w-full">
            <h5 class="text-xl font-medium text-h-neutral-900">
              {{ $t("forum.topic.denounce.title") }}
            </h5>
          </div>
        </template>
        <div>
          <div>
            <p class="mx-4 mt-4 mb-6 font-semibold text-h-neutral-900">
              {{ $t("forum.topic.denounce.labels.description") }}
            </p>
          </div>
          <div
            v-for="denounce in itemsDenounce"
            :key="denounce.key"
            class="flex mb-4 ml-4 align-items-center"
          >
            <RadioButton
              v-model="selectedDenounce"
              :inputId="denounce.key"
              name="dynamic"
              :value="$t('labels.generic', {pt: denounce.labelPT, en: denounce.labelEN})"
            />
            <label :for="denounce.key" class="ml-2 font-semibold">
              <p class="p-0 text-h-neutral-900">
                {{ $t("labels.generic", {pt: denounce.labelPT, en: denounce.labelEN}) }}
              </p>
            </label>
          </div>
        </div>
        <template #footer>
          <div>
            <Button
              class="btn-outline !h-8"
              @click="toggleDenounceDialog(false)"
            >
              {{ $t("forum.topic.denounce.buttons.cancel") }}
            </Button>
            <Button
              class="btn-primary !h-8"
              @click="denounceTopic"
              :disabled="!selectedDenounce"
            >
              {{ $t("forum.topic.denounce.buttons.sendReport") }}
            </Button>
          </div>
        </template>
      </Dialog>
    </section>
  </main>
</template>

<script lang="ts">
import { Comment } from "@/models/comment.model";
import { defineAsyncComponent, defineComponent } from "vue";
import { ForumService } from "../forum.service";
import { QueryParams } from "@/models/query-params.model";
import { useRoute } from "vue-router";
import { StatusTopic, Topic } from "@/models/topic.model";
import { GoToRoute } from "@/utils/go-to.util";
import { ToastFactory } from "@/utils/toast-factory.util";
import { Messages } from "@/utils/messages.util";
import { take } from "rxjs";
import { itemsDenounce } from "@/views/CourseStudent/Forum/utils/denounce.utils";
import { getUserFromStorage } from "@/utils/user-store.util";
import { Roles } from "@/models/user.model";

const ListComments = defineAsyncComponent(
  () => import('@/views/CourseStudent/Forum/TopicDetail/ListComments/list-comments.vue')
);
const CommentInput = defineAsyncComponent(
  () => import('@/views/CourseStudent/Forum/components/CommentInput/comment-input.vue')
);
const TopicCardDetailed = defineAsyncComponent(
  () => import('@/views/CourseStudent/Forum/TopicDetail/TopicCardDetail/topic-card-detailed.vue')
);
const BreadcrumbForum = defineAsyncComponent(
  () => import('@/views/CourseStudent/Forum/components/BreadcrumbForum/breadcrumb-forum.vue')
)

export default defineComponent({
  name: "topic-detail",
  components: {
    ListComments,
    CommentInput,
    TopicCardDetailed,
    BreadcrumbForum
  },
  data() {
    return {
      comments: [] as Array<Comment>,
      totalComments: 0,
      status: StatusTopic,
      numberOfCommentsPerPage: 0,
      query: new QueryParams(),
      route: useRoute(),
      topicId: String(this.$route.params.topicId),
      topic: {} as Topic,
      replyOfComment: new Comment(),
      isTopicLoading: false,
      params: {
        courseName: this.$route.params.courseName as string,
        id: this.$route.params.id as string,
      },
      selectedDenounce: "",
      isDenounceDialogVisible: false,
      itemsDenounce: itemsDenounce,
      home: { label: "Lista de fóruns", command: () => this.goToForum() },
      items: [{ label: this.capitalizeText(this.$route.params.courseName as string), command: () => this.backToForumView() }],
    };
  },
  mounted() {
    this.getRouteParams();
    this.getAllComments();
  },
  methods: {
    getAllComments(): void {
      this.service.comments.pipe().subscribe({
        next: (response) => {
          if (response) {
            this.comments = response.content?.[0].comments;
            this.topic = response.content?.[0].topic;
            this.totalComments = response.total;
            this.numberOfCommentsPerPage = response.size;
          }
          this.isTopicLoading = false;
        },
      });
      this.isTopicLoading = true;
      this.service.getAllCommentsByTopic(this.query, this.topicId);
    },
    getRouteParams(): void {
      if (this.$route.query) {
        this.query = {
          page: Number(this.$route.query.page) || 0,
          size: this.numberOfCommentsPerPage || 10,
        };
      }
    },
    changePage(query: QueryParams): void {
      this.query = { ...this.query, page: query.page, size: query.size };
      this.$router.push({
        name: "topicDetail",
        query: { ...this.$route.query, page: query.page, size: query.size },
      });
      this.getAllComments();
    },
    refreshComment(): void {
      this.getAllComments();
    },
    replyComment(replyComment: Comment): void {
      this.replyOfComment = replyComment;
    },
    removeReplyComment(): void {
      this.replyOfComment = new Comment();
    },
    isSubmittingComment(isSubmitting: boolean): void {
      this.isTopicLoading = isSubmitting;
    },
    commentCreated(): void {
      this.getAllComments();
    },
    backToForumView(): void {
      this.go.redirectToPage("forum", this.params);
    },
    denounceTopic(): void {
      this.service.topic.pipe(take(1)).subscribe({
        next: (response) => {
          if (response) {
            this.$toast.add(ToastFactory.success(Messages.TOPIC_DENOUNCE));
          }
        },
      });
      this.service.denounceTopic(this.$route.params.topicId as string, {
        description: this.selectedDenounce,
      });
      this.isDenounceDialogVisible = false;
    },
    toggleDenounceDialog(state: boolean) {
      this.selectedDenounce = "";
      this.isDenounceDialogVisible = state;
    },
    capitalizeText(text: string): string {
      const replace = text.replaceAll("-", " ");
      return replace;
    },
    isTeacherOrAdmin(): boolean {
      const userRole = getUserFromStorage()?.role;
      return (userRole === Roles.ADMINISTRATOR || userRole === Roles.TEACHER);
    },
  },
  computed: {
    service(): ForumService {
      return new ForumService();
    },
    go(): GoToRoute {
      return new GoToRoute();
    },
  },
});
</script>
